import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Card, Chip, EmptyState, Input, Markdown, PageHeader, PageLoading, YouTubeEmbed } from '@koyomi/ui';
import { LEGAL, markFaqViewed, useHelpHome, useHelpSearch, useHelpTutorial, type HelpFaq, type HelpTutorialSummary } from './help-api.ts';
import './help.css';

/** Central de Ajuda (roadmap §2): busca, categorias, FAQ em acordeão (com "ver tutorial") e tutoriais. */
export function HelpPage() {
  const { t } = useTranslation(['common']);
  const home = useHelpHome();
  const [q, setQ] = useState('');
  const [cat, setCat] = useState<string | null>(null);
  const search = useHelpSearch(q);
  const searching = q.trim().length >= 2;
  if (home.isLoading) return <><PageHeader title={t('common:help.title')} /><PageLoading cards={2} rows={5} /></>;
  const d = home.data;
  const faqs = searching ? search.data?.faqs ?? [] : (d?.faqs ?? []).filter((f) => !cat || f.categoryId === cat);
  const tutorials = searching ? search.data?.tutorials ?? [] : (d?.tutorials ?? []).filter((x) => !cat || x.categoryId === cat);
  const empty = !faqs.length && !tutorials.length;
  return (
    <>
      <PageHeader title={t('common:help.title')} subtitle={t('common:help.subtitle')} />
      <div className="help">
        <Input aria-label={t('common:help.search')} placeholder={t('common:help.searchPlaceholder')} value={q} onChange={(e) => setQ(e.target.value)} type="search" />
        {!searching && (d?.categories.length ?? 0) > 0 && (
          <div className="k-row" style={{ gap: 6, flexWrap: 'wrap' }}>
            <button type="button" className={`help__cat ${!cat ? 'help__cat--on' : ''}`} onClick={() => setCat(null)}>{t('common:help.all')}</button>
            {d!.categories.map((c) => <button key={c.id} type="button" className={`help__cat ${cat === c.id ? 'help__cat--on' : ''}`} onClick={() => setCat(c.id)}>{c.name}</button>)}
          </div>
        )}
        {empty ? <EmptyState title={searching ? t('common:help.noResults') : t('common:help.empty')} description={t('common:help.emptyHint')} /> : (
          <div className="help__grid">
            {faqs.length > 0 && <Card title={t('common:help.faqs')}><FaqList faqs={faqs} /></Card>}
            {tutorials.length > 0 && <Card title={t('common:help.tutorials')}><TutorialList tutorials={tutorials} /></Card>}
          </div>
        )}
        <p className="k-muted help__legal"><a href={LEGAL.terms} target="_blank" rel="noopener noreferrer">{t('common:help.terms')}</a> · <a href={LEGAL.privacy} target="_blank" rel="noopener noreferrer">{t('common:help.privacy')}</a></p>
      </div>
    </>
  );
}

export function FaqList({ faqs, onNavigate }: { faqs: HelpFaq[]; onNavigate?: () => void }) {
  const { t } = useTranslation(['common']);
  return (
    <div className="help__faqs">
      {faqs.map((f) => (
        <details key={f.id} className="help__faq" onToggle={(e) => { if ((e.target as HTMLDetailsElement).open) markFaqViewed(f.id); }}>
          <summary>{f.question}</summary>
          <Markdown source={f.answerMd} />
          {f.tutorial && <Link className="help__more" to={`/ajuda/tutorial/${f.tutorial.slug}`} onClick={onNavigate}>{t('common:help.seeTutorial', { title: f.tutorial.title })} ›</Link>}
        </details>
      ))}
    </div>
  );
}

export function TutorialList({ tutorials, onNavigate }: { tutorials: HelpTutorialSummary[]; onNavigate?: () => void }) {
  const { t } = useTranslation(['common']);
  return (
    <ul className="help__tutorials">
      {tutorials.map((x) => (
        <li key={x.id}>
          <Link to={`/ajuda/tutorial/${x.slug}`} onClick={onNavigate}><strong>{x.title}</strong></Link>
          {x.summary && <div className="k-muted" style={{ fontSize: 13 }}>{x.summary}</div>}
          {x.videoCount > 0 && <Chip tone="outline">{t('common:help.videos', { count: x.videoCount })}</Chip>}
        </li>
      ))}
    </ul>
  );
}

export function TutorialPage() {
  const { slug = '' } = useParams();
  const { t, i18n } = useTranslation(['common']);
  const navigate = useNavigate();
  const q = useHelpTutorial(slug);
  const back = <Button onClick={() => navigate('/ajuda')}>‹ {t('common:help.title')}</Button>;
  if (q.isLoading) return <><PageHeader title={t('common:help.title')} actions={back} /><PageLoading cards={1} rows={8} /></>;
  if (!q.data) return <><PageHeader title={t('common:help.title')} actions={back} /><EmptyState title={t('common:help.notFound')} /></>;
  const d = q.data;
  return (
    <>
      <PageHeader title={d.title} subtitle={d.summary ?? undefined} actions={back} />
      <div className="help help--article">
        {d.videos.map((v) => <YouTubeEmbed key={v.youtubeId} id={v.youtubeId} title={v.title} />)}
        {d.bodyMd && <Card><Markdown source={d.bodyMd} /></Card>}
        {d.faqs.length > 0 && <Card title={t('common:help.related')}><ul className="help__tutorials">{d.faqs.map((f) => <li key={f.id}>{f.question}</li>)}</ul></Card>}
        <p className="k-muted" style={{ fontSize: 12 }}>{t('common:help.updated', { date: new Date(d.updatedAt).toLocaleDateString(i18n.language) })}</p>
      </div>
    </>
  );
}
